import React from "react";
import styles from "./TermsPage.module.css";

export default function TermsPage() {
  return (
    <div className={styles.container}>
      <h1 className={styles.title}>TERMS & CONDITIONS — My AI Agent</h1>
      <p className={styles.lastUpdated}>Last Updated: January 2025</p>
      <p><em>(Also not legal advice; publish only after reviewing with an attorney.)</em></p>

      {/* Section 1 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>1. Agreement to Terms</h2>
        <p>
          By accessing or using the My AI Agent website or services, you agree to these Terms & Conditions.
          If you do not agree, do not use the service.
        </p>
      </div>

      {/* Section 2 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>2. Services Provided</h2>
        <ul className={styles.list}>
          <li>Missed call text-back automation</li>
          <li>AI messaging automation</li>
          <li>Scheduling features</li>
          <li>CRM integrations</li>
          <li>Communication workflows</li>
        </ul>
        <p>We may add or modify features at any time.</p>
      </div>

      {/* Section 3 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>3. Use of the Service</h2>
        <p>You agree to:</p>
        <ul className={styles.list}>
          <li>Use the service legally and ethically</li>
          <li>Not abuse or spam the messaging system</li>
          <li>Only send SMS messages to contacts who have a legitimate business relationship</li>
          <li>Provide accurate information when registering</li>
          <li>Not attempt to reverse-engineer or disrupt our systems</li>
        </ul>
        <p>
          You are responsible for compliance with federal and local communication laws (TCPA, carrier rules, etc.).
        </p>
      </div>

      {/* Section 4 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>4. Account Responsibilities</h2>
        <p>You must maintain:</p>
        <ul className={styles.list}>
          <li>A secure password</li>
          <li>Accurate account details</li>
          <li>Authorized access to integrated systems (CRM, phone provider, etc.)</li>
        </ul>
        <p>You are responsible for activity under your account.</p>
      </div>

      {/* Section 5 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>5. Billing & Payments</h2>
        <ul className={styles.list}>
          <li>Subscriptions are billed in advance.</li>
          <li>We do not offer refunds for partial billing periods.</li>
          <li>All fees are subject to change with notice.</li>
        </ul>
      </div>

      {/* Section 6 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>6. Data & Privacy</h2>
        <p>Your use of the service is governed by our Privacy Policy.</p>
        <p>You grant us permission to process data necessary to operate the service.</p>
      </div>

      {/* Section 7 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>7. Service Availability</h2>
        <p>We strive for maximum uptime but do not guarantee:</p>
        <ul className={styles.list}>
          <li>Uninterrupted service</li>
          <li>Error-free operation</li>
          <li>Delivery of all messages</li>
        </ul>
        <p>
          We are not liable for delays, outages, or issues caused by carriers or third-party services.
        </p>
      </div>

      {/* Section 8 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>8. Limitation of Liability</h2>
        <p>To the fullest extent permitted by law, My AI Agent is not liable for:</p>
        <ul className={styles.list}>
          <li>Lost profits</li>
          <li>Lost data</li>
          <li>Business interruption</li>
          <li>Damages resulting from misuse or outages</li>
        </ul>
        <p>Your sole remedy is to discontinue use of the service.</p>
      </div>

      {/* Section 9 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>9. Termination</h2>
        <p>We may suspend or terminate your access if you:</p>
        <ul className={styles.list}>
          <li>Violate these Terms</li>
          <li>Use the service for spam or illegal purposes</li>
          <li>Abuse integrations or cause system instability</li>
        </ul>
        <p>You may cancel at any time.</p>
      </div>

      {/* Section 10 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>10. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the State of [Insert State].  
          Any disputes will be resolved in that jurisdiction.
        </p>
      </div>

      {/* Section 11 */}
      <div className={styles.section}>
        <h2 className={styles.heading}>11. Contact Us</h2>
      </div>

      {/* TCPA / A2P Section */}
      <h2 className={styles.heading}>TCPA & A2P 10DLC Compliance Disclaimers</h2>
      <p><em>(Use on your website, onboarding materials, and signup flow.)</em></p>

      {/* Disclaimer 1 */}
      <div className={styles.section}>
        <h3 className={styles.subheading}>1. General TCPA Compliance Disclaimer</h3>
        <p>
          By using the My AI Agent platform, you agree that all SMS communications sent through our system must comply with the Telephone Consumer Protection Act (TCPA), FCC regulations, and all applicable state and carrier rules.
        </p>
        <p>You are responsible for:</p>
        <ul className={styles.list}>
          <li>Obtaining proper consent before messaging consumers</li>
          <li>Maintaining records of express consent</li>
          <li>Ensuring all outbound messaging is related to your business</li>
          <li>Following required opt-in and opt-out guidelines</li>
          <li>Using the service only for lawful, customer-initiated communications</li>
        </ul>
        <p>
          My AI Agent provides tools to help support compliance, but you are solely responsible for how you use the system.
        </p>
      </div>

      {/* Disclaimer 2 */}
      <div className={styles.section}>
        <h3 className={styles.subheading}>2. A2P 10DLC Compliance Disclaimer</h3>
        <p>All users must comply with A2P 10DLC registration requirements, including:</p>
        <ul className={styles.list}>
          <li>Providing accurate business information</li>
          <li>Registering your brand and campaign(s)</li>
          <li>Using truthful sample messages</li>
          <li>Maintaining an approved use case</li>
        </ul>
        <p>Failure to maintain A2P compliance may result in:</p>
        <ul className={styles.list}>
          <li>Filtering</li>
          <li>Delivery failures</li>
          <li>Carrier penalties</li>
          <li>Suspension of messaging capabilities</li>
        </ul>
        <p>My AI Agent assists with registration but does not guarantee carrier approval.</p>
      </div>

      {/* Disclaimer 3 */}
      <div className={styles.section}>
        <h3 className={styles.subheading}>3. Consent (Opt-In) Requirement Disclaimer</h3>
        <p>You must only send SMS messages to individuals who have provided one of the following:</p>
        <ul className={styles.list}>
          <li>Express written consent</li>
          <li>Web form consent</li>
          <li>Verbal consent (documented)</li>
          <li>Existing business relationship with legitimate interest</li>
        </ul>
        <p>Cold texting, purchased lists, or unsolicited marketing messages are strictly prohibited.</p>
        <p>You must maintain records proving consent.</p>
      </div>

      {/* Disclaimer 4 */}
      <div className={styles.section}>
        <h3 className={styles.subheading}>4. Opt-Out Compliance Disclaimer</h3>
        <p>All SMS messages sent through the platform must include clear opt-out instructions, such as:</p>
        <p><span className={styles.highlight}>“Reply STOP to opt out.”</span></p>
        <p>When a recipient opts out:</p>
        <ul className={styles.list}>
          <li>You must immediately cease sending messages</li>
          <li>The opt-out must be honored across all campaigns</li>
          <li>Requiring additional steps is prohibited</li>
        </ul>
        <p>My AI Agent automatically handles STOP/START flows, but you are accountable for compliance.</p>
      </div>

      {/* Disclaimer 5 */}
      <div className={styles.section}>
        <h3 className={styles.subheading}>5. Message Content Disclaimer</h3>
        <p>You agree not to send:</p>
        <ul className={styles.list}>
          <li>Unsolicited marketing messages</li>
          <li>Spam or mass promotional campaigns</li>
          <li>Threatening, abusive, or unlawful content</li>
          <li>SHAFT content (Sex, Hate, Alcohol, Firearms, Tobacco)</li>
          <li>Sensitive information (HIPAA-protected, financial, etc.)</li>
        </ul>
        <p>Your messaging must align with your approved A2P 10DLC campaign use case.</p>
      </div>

      {/* Disclaimer 6 */}
      <div className={styles.section}>
        <h3 className={styles.subheading}>6. Data Protection Disclaimer</h3>
        <p>By using My AI Agent, you agree to abide by federal and state privacy laws.</p>
        <p>You are responsible for:</p>
        <ul className={styles.list}>
          <li>Ensuring customer data is collected legally</li>
          <li>Preventing unauthorized use of personally identifiable information</li>
          <li>Not uploading or using purchased or unverified contact lists</li>
        </ul>
        <p>My AI Agent uses secure systems but cannot be held responsible for misuse of data you import.</p>
      </div>

      {/* Disclaimer 7 */}
      <div className={styles.section}>
        <h3 className={styles.subheading}>7. Carrier & Delivery Disclaimer</h3>
        <p>Message delivery is dependent on carriers and third-party networks. My AI Agent does not guarantee:</p>
        <ul className={styles.list}>
          <li>Delivery of messages</li>
          <li>Delivery speed</li>
          <li>Delivery outside of approved use cases</li>
        </ul>
        <p>Carriers may block or filter messages they deem noncompliant.</p>
      </div>

      {/* Disclaimer 8 */}
      <div className={styles.section}>
        <h3 className={styles.subheading}>8. Indemnification Clause</h3>
        <p>By using My AI Agent’s SMS features, you agree to indemnify and hold harmless:</p>
        <ul className={styles.list}>
          <li>My AI Agent</li>
          <li>Its owners</li>
          <li>Its affiliates</li>
          <li>Its partners</li>
        </ul>
        <p>from any claims, penalties, fines, or damages related to:</p>
        <ul className={styles.list}>
          <li>TCPA violations</li>
          <li>A2P 10DLC violations</li>
          <li>Improper consent</li>
          <li>Unlawful texting activity</li>
        </ul>
        <p>You assume full responsibility for compliance.</p>
      </div>

      {/* Contact Info */}
      <div className={styles.contactInfo}>
        <p><strong>Email:</strong> <a className={styles.link} href="mailto:myaiagentbiz@gmail.com">myaiagentbiz@gmail.com</a></p>
        <p><strong>Website:</strong> <a className={styles.link} href="https://myaiagent.biz" target="_blank" rel="noopener noreferrer">myaiagent.biz</a></p>
      </div>
    </div>
  );
}
